/** Our library module */
export module L {

    export interface ILiteEvent<T> {
        on(handler: { (data?: T): void }) : void;
        off(handler: { (data?: T): void }) : void;
    }
    export class Ev<T> implements ILiteEvent<T> {
        private handlers: { (data?: T): void; }[] = [];
    
        public on(handler: { (data?: T): void }) : void {
            this.handlers.push(handler);
        }
    
        public off(handler: { (data?: T): void }) : void {
            this.handlers = this.handlers.filter(h => h !== handler);
        }
    
        public trigger(data?: T) {
            this.handlers.slice(0).forEach(h => h(data));
        }
    
        public expose() : ILiteEvent<T> {
            return this;
        }
    }

    export function isLocalNetwork(hostname = window.location.hostname) {
        return (
            (['localhost', '127.0.0.1', '', '::1'].includes(hostname))
            || (hostname.startsWith('192.168.'))
            || (hostname.startsWith('10.0.'))
            || (hostname.endsWith('.local'))
        )
    }

    export function isMobile(): boolean {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    export function dqs(str:string){
        return <HTMLElement>document.querySelector(str);
    }

    export function enumKeys<E>(e: E): (keyof E)[] {
        return Object.keys(e) as (keyof E)[];
      }


    /** document.querySelectorAll syntactical sugar, returns casted iterator*/
    export function* dqsa(sel: string): Generator<HTMLElement> {
        var elems = document.querySelectorAll(sel);
        for (var i = 0; i < elems.length; i++)
            yield <HTMLElement>elems[i];
    }

    /** converts, eg: ['joey', 'doug', 'harry'] into 'joey, doug, and harry'*/
    export function convertToCommaSeperatedString(vals: string[]) {
        if (vals.length == 0)
            return "";
        if (vals.length == 1)
            return vals[0];
        if (vals.length == 2)
            return vals[0] + " and " + vals[1];
        return vals.slice(0, vals.length - 1).join(', ') + ", and " + vals[vals.length - 1];
    }

    export function stringIsValidString(obj: any): obj is string {
        return (typeof obj === 'string' || <any>obj instanceof String);
    }

    /** eg: "xxx","y"=> "yxx", just overlays the second string onto the first */
    export function stringOverlay(originalString: string, stringToOverlay: string, overlayPos: number = 0) {
        var startPart = originalString.substr(0, overlayPos);
        var endPart = originalString.substr(stringToOverlay.length + overlayPos);
        return startPart + stringToOverlay + endPart;
    }

    /** e.g. [1,2,3] -> [1,2], [2,3] */
    export function arrayToNeighbors<T>(arr: Array<T>): Array<Array<T>> {
        var toReturn: Array<Array<T>> = [];
        for (var i = 0; i < arr.length; ++i)
            if (i > 0)
                toReturn.push([arr[i - 1], arr[i]]);
        return toReturn;
    }

    export function arraySpliceMultiple<T>(arr: T[], indeces: number[]): T[] {
        var toReturn: T[] = [];
        for (let i = indeces.length - 1; i >= 0; i--) { 
            toReturn.push(arr.splice(indeces[i], 1)[0]);
         }
        return toReturn;
    }

    /** Checks with == if the two arrays are the same, must be ordered the same */
    export function arrayEquals<T>(a: T[], b: T[]): boolean {
        if (a.length != b.length)
            return false;
        for (var i = 0; i < a.length; ++i) {
            if (a[i] !== b[i])
                return false;
        }
        return true;
    }

    export function addHours(start: Date, h: number): Date {
        var newTime = new Date(start);
        return new Date(newTime.getTime() + (h * 60 * 60 * 1000));
    }

    export function msToDisplayString(ms:number):string{
        var totalS = ms / 1000;
        var totalM = Math.floor(totalS / 60);
        var totalH = Math.round(totalS / (60 * 60));
        if (totalH > 0)
            return `${totalH}h`
        if (totalM > 0)
            return `${totalM}m`;
        return `${Math.ceil(totalS)}s`;
    }

    /** starts an exponential falloff at some offset */
    export function expFalloff(x: number, xOffset: number, expBase: number) {
        return Math.pow(expBase, (1 - Math.max(x, xOffset) / xOffset));
    }

    function randn_bm() {
        var u = 0, v = 0;
        while (u === 0) u = Math.random(); //Converting [0,1) to (0,1)
        while (v === 0) v = Math.random();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    }

    export function bellcurve(mid: number, std: number) {
        return randn_bm() * std + mid;
    }

    export var DEGREE_TO_RAD = 0.0174532925;
    export var RAD_TO_DEGREE = 57.2957795;
    /** inclusive clamp. Clamp(100,0,5)=5, Clamp(-100,0,5)=0, Clamp(2,0,5)=2 */
    export function Clamp(x: number, a: number, b: number): number {
        if (x < a)
            return a;
        if (x > b)
            return b;
        return x;
    }


    export function ArraySelectRandom<T>(array: T[]): T {
        return array[Math.floor(Math.random() * array.length)];
    }

    /** Select the element of the array closest to val (absolute distance) */
    export function ArraySelectMax<T>(array: T[], func: (val: T) => number): T {
        var max = Number.MIN_SAFE_INTEGER;
        var elem!: T;
        for (var i = 0; i < array.length; ++i) {
            if (func(array[i]) >= max) {
                max = func(array[i]);
                elem = array[i];
            }
        }
        return elem;
    }

    /** Select the element of the array closest to val (absolute distance) */
    export function ArraySelectMin<T>(array: T[], func: (val: T) => number): T {
        var min = Number.MAX_SAFE_INTEGER;
        var elem!: T;
        for (var i = 0; i < array.length; ++i) {
            if (func(array[i]) <= min) {
                min = func(array[i]);
                elem = array[i];
            }
        }
        return elem;
    }

    /**    Return evenly spaced numbers over a specified interval. */
    export function ArrayLinspace(start: number, end: number, num: number) {
        var toReturn = [];
        for (var i = 0; i < num; ++i) {
            toReturn.push((i / (num - 1)) * (end - start))
        }
        return toReturn;
    }


    export function ArrayShuffleInPlace(array: any[]) {
        var currentIndex = array.length, temporaryValue, randomIndex;
      
        // While there remain elements to shuffle...
        while (0 !== currentIndex) {
      
          // Pick a remaining element...
          randomIndex = Math.floor(Math.random() * currentIndex);
          currentIndex -= 1;
      
          // And swap it with the current element.
          temporaryValue = array[currentIndex];
          array[currentIndex] = array[randomIndex];
          array[randomIndex] = temporaryValue;
        }
      
        return array;
      }

            /** flatten would be ([[1,2,3],[4,5,6]], a=>a), essentially it performs some operation over the elements and then combines everything flat */
    export function arraySelectMany<T, U>(x: T[], lambda: (a: T) => U[]): U[] {
        var toReturn: U[] = [];
        for (var i = 0; i < x.length; ++i) {
            toReturn.push(...lambda(x[i]));
        }
        return toReturn;
    }

    /** Returns true if any element satisfies a condition. L.ArrayAny([1,2,3],n=>n==3) would return true */
    export function arrayAny<T>(x: T[], lambda: (a: T) => boolean): boolean {
        for (var i = 0; i < x.length; ++i) {
            if (lambda(x[i]))
                return true;
        }
        return false;
    }

    /** returns the min based off some function*/
    export function arrayMin<T>(x: Array<T>, lambda: (x: T) => number): number {
        var min = Number.MAX_SAFE_INTEGER;
        for (var i = 0; i < x.length; ++i) {
            var val = lambda(x[i]);
            if (val < min) {
                min = val;
            }
        }
        return min;
    }

    /** returns the max based off some function*/
    export function ArrayMax<T>(x: Array<T>, lambda: (x:T) => number): T {
        var max = Number.MIN_SAFE_INTEGER;
        var toReturn = x[0];
        for (var i = 0; i < x.length; ++i) {
            var val = lambda(x[i]);
            if (val > max) {
                toReturn = x[i];
                max = val;
            }
        }
        return toReturn;
    }

    /** returns the max based off some function*/
    export function ArraySum<T>(x: Array<T>, lambda: (x: T) => number): number {
        var sum = 0;
        for (var i = 0; i < x.length; ++i) {
            sum += lambda(x[i]);
        }
        return sum;
    }

    /** returns the max based off some function*/
    export function ArrayAvg<T>(x: Array<T>, lambda: (x: T) => number): number {
        var sum = 0;
        for (var i = 0; i < x.length; ++i) {
            sum += lambda(x[i]);
        }
        return sum / x.length;
    }

    /** clamps an index to the bounds of the array */
    export function ArrayClamp<T>(x: Array<T>, i: number): T {
        return x[L.Clamp(i, 0, x.length - 1)];
    }

    export function Quartic(start: number, end: number, t: number): number {
        t = Math.max(0, Math.min(1, t));
        let output = start + (end - start) * (1 - (--t) * t * t * t);
        return output;
    }

    export function Sigmoid(t: number) {
        let output = 1 / (1 + Math.exp(-t));
        return output;
    }

    export function Mean(array: Array<number>) {
        return array.reduce((a, b) => a + b, 0) / array.length;
    }

    export function Std(array: number[]) {
        const n = array.length
        const mean = array.reduce((a, b) => a + b) / n
        return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n)
    }

    export function Lerp(a: number, b: number, t: number) {
        return L.Clamp(a + (b - a) * t, a, b);
    }

    export function EasingLinear(t: number) {
        return t;
    }
    export function EasingQuartic(t:number){
        return L.Quartic(0, 1, t);
    }

    export function EasingExp(t:number){
        t = L.Clamp(t,0, 1);
        return L.Clamp(1 - Math.pow(20, (1 - Math.max(t, .5) / .5)), 0, 1);
    }

    /** find the % a number is along */
    export function TNorm(t:number, from:number, to:number){
        return L.Clamp((t - from) / (to - from), 0, 1);
    }

    /** This listener lets you listen for buttons asynchronously, it queues button presses so no user hotkey is missed, it also allows you to inject keypress for debugging purposes */
    export class ButtonListener<T extends string[], U extends T[number]> {
        queue: U[] = [];
        public defaultHapticDurMS = 0;
        public TriggerButtons(...buttons: string[]) {
            this.queue.push(...<U[]>buttons);
            this.processQueue();
        }
        public SendHotkeys(...hotkeys: string[]) {
            for (var key of hotkeys)
                if (this.hotkeys[key] !== undefined){
                    var items = <U[]>this.hotkeys[key];
                    this.queue.push(...items);
                }
            this.processQueue(true);
        }
        private readonly ids: T;
        constructor(...ids: T) {
            this.ids = ids;
        }
        /**You must bind to the on click events, this is needed because you don't want to have to pass around the long and complicated type associated with this guy into other methods (e.g. this class is meant to be global, thus you need to declare it before onload and bind after onload) */
        public Bind(){
            for (let id of this.ids) {
                dqs(id).onclick = ev => {
                    this.upd(<U>id);
                };
            }
            document.addEventListener('keydown', (e: KeyboardEvent) => this.SendHotkeys(e.key), false);
        }

        private upd = (id: U) => {
            this.queue.push(<U>id);
            this.processQueue();
        }
        private hotkeys: { [k: string]: U[] } = {};
        public SetHotkeys(dict: { [k: string]: U[] }) {
            this.hotkeys = dict;
        }
        private durations: { [k: string]: number } = {};
        public SetHapticDur(dur:number, ...ids: U[]) {
            for (var id of ids) {
                this.durations[id] = dur;
            }
        }
        private processQueue(wasKeyboard: boolean = false) {
            if (this.promise_accept != null) {
                //find the first click of what we are waiting for
                var lowestIndex = Number.MAX_SAFE_INTEGER;
                var bestKey = null;
                for (var i = 0; i < this.waiting_for.length; ++i) {
                    var waitingFor = this.waiting_for[i];
                    var idx = this.queue.findIndex(a => a == waitingFor);
                    if (idx != -1 && idx < lowestIndex) {
                        lowestIndex = idx;
                        bestKey = waitingFor;
                    }
                }

                //discard unused inputs and invoke the promise
                if (bestKey != null) {
                    this.queue.splice(0, lowestIndex + 1);
                    if (wasKeyboard) {
                        if ((<HTMLInputElement>dqs(bestKey)).checked !== undefined) //force check the checkboxes
                            (<HTMLInputElement>dqs(bestKey)).checked = !(<HTMLInputElement>dqs(bestKey)).checked;
                    }
                    if (!wasKeyboard && L.isMobile()) {
                        if(this.durations[bestKey])
                            TryHapticMs(this.durations[bestKey]);
                        else
                            TryHapticMs(this.defaultHapticDurMS);
                    }
                    this.promise_accept(bestKey);
                }
            }
        }

        promise_accept: (string) => void = null;
        waiting_for: U[] = [];
        /** Clicks are queued up, If you click on X and then Y while X is still processing they both happen. */
        Click(...ids: U[]): Promise<U> {
            if (this.promise_accept != null)
                throw new Error("Can't listen for multiple clicks at the same time");
            return new Promise<U>((accept, reject) => {
                this.waiting_for = ids;
                this.promise_accept = (clickedId) => {
                    accept(clickedId);
                    this.promise_accept = null;
                    this.waiting_for = [];
                }
                this.processQueue();
            });
        }
    }
    const canVibrate = window.navigator.vibrate;
    /** Try to shake the phone for the specified number of MS, does nothing if it fails */
    export function TryHapticMs(ms: number) {
        try{
            if (canVibrate){
                if (ms == 0) {
                    return;
                }
                window.navigator.vibrate(ms);
            }
        }catch{
            //do nothing, we are just trying to shake here
        }
    }

	export class Header {
			name: string;
			value: string;
			constructor(header: string, data: string) {
					this.name = header;
					this.value = data;
			}
	}

export interface Data {
	Headers: string;
	Text: string;
	Type: string;
	Status: number;
	StatusText: string;
	ErrorMessage?: string;
}
export function isData(obj: any): obj is Data {
	if (obj !== undefined &&
		(<Data>obj).Headers !== undefined &&
		(<Data>obj).Text !== undefined &&
		(<Data>obj).Type !== undefined &&
		(<Data>obj).Status !== undefined &&
		(<Data>obj).StatusText !== undefined)
			return true;
	return false
}
export function XhrErrorMessage(obj:any){
	if (!isData(obj))
		return null;
	return `${obj.Status}: ${obj.Text}`;
}

function DataFromJSXHR(jsXHR: XMLHttpRequest): Data {
	var data: Data = {
		Headers: jsXHR.getAllResponseHeaders(),
		Text: jsXHR.responseText,
		Type: jsXHR.responseType,
		Status: jsXHR.status,
		StatusText: jsXHR.statusText
	};
	return data;
}

function SendCommandInternal(
	finish: (data: Data) => void,
	method: string,
	url: string,
	headers: Array<Header>,
	data: string = ""): void {
		var jsXHR = new XMLHttpRequest();
		jsXHR.open(method, url);

		headers.forEach(header =>
			jsXHR.setRequestHeader(header.name, header.value));

		jsXHR.onload = (ev) => {
			if (jsXHR.status < 200 || jsXHR.status >= 300) {
				var data = DataFromJSXHR(jsXHR);
				data.ErrorMessage = `${jsXHR.responseText} (#${jsXHR.status})`;
				finish(data);
			}else{
				finish(DataFromJSXHR(jsXHR));
			}
		}
		jsXHR.onerror = (ev) => {
			var data = DataFromJSXHR(jsXHR);
			//the error info isn't included here for whatever reason lol...
			data.ErrorMessage = 'Unspecified Error ' + method.toUpperCase() + 'ing to url \'' + url + '\', check the browser js console for more info';
			finish(data);
		};

		if (method == 'POST')
			jsXHR.send(data);
		else
			jsXHR.send();
}
function SendCommand(
	method: string,
	url: string,
	headers: Array<Header>,
	data: string = "",
	retries = 0): Promise<Data> {
	return new Promise(function (resolve, reject) {
		var tryToSend = (triesLeft) => {
			SendCommandInternal((data) => {
				if (data.ErrorMessage) {
					if (triesLeft > 1)
						setTimeout(() => tryToSend(triesLeft - 1), 2500);
					else
						reject(data.ErrorMessage);
				} else {
					resolve(data);
				}
			}, method, url, headers, data);
		}
		tryToSend(retries);
	});
}

/** Performs GET on `url` returning the deserialized object of type `T`. Retries `retries` # of times before throwing. Internally done via json.
 * 
 * `var time:{time:string} = await Get('api/serverTime');`
 * 
 * `time = await Get('https://blah.com/api/serverTime');`
 * */
export async function Get<T>(
	url: string,
	retries: number = 0,
	headers: Array<Header> = []): Promise<T> {

	return new Promise((resolve, reject) => {
		if (!headers.some(a => a.name.toLowerCase() == "Content-Type".toLowerCase()))
			headers.push({ name: 'Content-Type', value: 'application/json;charset=UTF-8' });
		SendCommand('GET', url, headers, "", retries).then(data => {
			try{
				(<DataWithObj<T>>data).obj = <any>JSON.parse(data.Text);
				resolve((<DataWithObj<T>>data).obj);
			} catch (er) {
				reject("error deserializing web request to " + url);
			}
		}, er => {
			reject(er);
		})
	});

}

/** posts string to url */
export async function PostString(url: string, data: string, headers: Array<Header> = []): Promise<Data> {
	return SendCommand('POST', url, headers, data);
}

/** posts object to url, returns the web request */
export async function PostJson(url: string, data: object = null, headers: Array<Header> = [], retries = 0): Promise<Data> {
	var jsonData = JSON.stringify(data);
	if (!headers.some(a => a.name.toLowerCase() == "Content-Type".toLowerCase()))
		headers.push({ name: 'Content-Type', value: 'application/json;charset=UTF-8' });
	return SendCommand('POST', url, headers, jsonData, retries);
}

export interface DataWithObj<T> extends Data {
	obj: T
}
/** posts `data` to `url` via JSON (you don't need to serialize). Returns deserialized object, fails with reject (after `retries` are exhauted)
 * 
 * `(await Post({url:'api/sqrt',data:{val:4}})).r; //2`*/
export async function Post<T>({ url, data = null, retries = 0, headers = [] }: { url: string, data?: object, retries?: number, headers?: Header[] }): Promise<T> {
    return new Promise((resolve, reject) => {
        var jsonData = JSON.stringify(data);
        if (!headers.some(a => a.name.toLowerCase() == "Content-Type".toLowerCase()))
            headers.push({ name: 'Content-Type', value: 'application/json;charset=UTF-8' });
        SendCommand('POST', url, headers, jsonData, retries).then(data => {
            try {
                (<DataWithObj<T>>data).obj = <any>JSON.parse(data.Text);
                resolve((<DataWithObj<T>>data).obj);
            } catch (er) {
                reject("error deserializing web request to " + url);
            }
        }, er => {
            reject(er);
        })
    });

}

}